import { useDispatch, useSelector } from "react-redux";
import { createDistributors, createManufactures, createOrUpdateItem, fetchBranchList, fetchDistributors, fetchGenericNames, fetchItemCategories, fetchItemCode, fetchItemGroups, fetchItemModels, fetchItemOrigins, fetchItemRacks, fetchItemsList, fetchItemUnits, fetchManufactures } from "../../itemManageThunks";
import { App, Button, Checkbox, Form, Input, InputNumber, Modal, Select, Upload } from "antd";
import { selectBranchList, selectItemCategories, selectItemDistributors, selectItemManageItemCode, selectItemManufactures, selectItemModels, selectItemOrigins, selectItemRacks, selectItemUnits } from "../../itemManageSelectors";
import SearchListAutoComplete from "../../../../components/SearchListAutoComplete";
import { selectAuthInfo } from "../../../Auth/authSelectors";
import { useEffect, useState, useRef, useMemo } from "react";
import { fetchItems } from "../../../../store/commonThunks/lookups";
import store from "../../../../store";
import InputNumberField from "../../../../components/Input/InputNumber";
import { saveGenericName } from "../../../GenericNames/genericNameThunks";
import JsBarcode from "jsbarcode";

import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { createOrUpdateUnit } from "../../../Units/unitsThunks";
import AddMasterModal from "../AddMasterModal";
import SelectField from "../../../../components/Input/Select";
import InputField from "../../../../components/Input/Input";
import { Option } from "antd/es/mentions";

const ProductQuickForm = ({ form, onSuccess, fromServiceEntry = false }) => {
  const [openManufactureModal, setOpenManufactureModal] = useState(false);
  const [openDistributorModal, setOpenDistributorModal] = useState(false);
  const [manufactureName, setManufactureName] = useState("");
  const [manufactureGcpLength, setManufactureGcpLength] = useState(null);
  const [distributorName, setDistributorName] = useState("");
  const [openMasterModal, setOpenMasterModal] = useState(false);
  const [pendingUnitName, setPendingUnitName] = useState(null);
  const [openGenericModal, setOpenGenericModal] = useState(false);
  const [genericName, setGenericName] = useState("");
  const barcodeCanvasRef = useRef(null);
  const [generatedBarcode, setGeneratedBarcode] = useState("");
  const [showBarcodePreview, setShowBarcodePreview] = useState(false);

  const dispatch = useDispatch();
  const genericNames = useSelector((state) => state.itemManage.genericNames);
  // Dedupe (by name, case-insensitive) and sort A→Z; backend can return
  // repeat rows when default generic names get re-seeded.
  const sortedGenericNames = useMemo(() => {
    const seen = new Set();
    return (genericNames ?? [])
      .filter((g) => {
        const key = g.generic_name?.trim().toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) =>
        a.generic_name.trim().localeCompare(b.generic_name.trim(), undefined, {
          sensitivity: "base",
        })
      );
  }, [genericNames]);


  const { modal, message } = App.useApp();
  const itemCode = useSelector(selectItemManageItemCode);
  const units = useSelector(selectItemUnits);
  const racks = useSelector(selectItemRacks);
  const groups = useSelector((state) => state.itemManage.groups);
  const categories = useSelector(selectItemCategories);
  const models = useSelector(selectItemModels);
  const origins = useSelector(selectItemOrigins);

  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);

  // Category depends on the chosen group, model / sub category on the chosen category.
  const selectedGroupId = Form.useWatch("group_id", form);
  const selectedCategoryId = Form.useWatch("category_id", form);
  const filteredCategories = useMemo(
    () => (categories || []).filter((c) => c.group_id === selectedGroupId),
    [categories, selectedGroupId]
  );
  const filteredModels = useMemo(
    () => (models || []).filter((m) => m.category_id === selectedCategoryId),
    [models, selectedCategoryId]
  );
  const filterByLabel = (input, option) =>
    (option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase());
  const branches = useSelector(selectBranchList);
  const manufactures = useSelector(selectItemManufactures);
  const distributors = useSelector(selectItemDistributors);
  const authInfo = useSelector(selectAuthInfo);

  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const isUAE = authInfo?.country === "United Arab Emirates";

  const isDrugStore = authInfo?.company_type === "Drug Store" && isUAE;
  const isPharmacy = authInfo?.company_type === "Pharmacy";

  // UI-only helper (never sent to the backend): enter a tax-inclusive price and
  // derive the ex-tax Retail Rate.
  const [priceInclGst, setPriceInclGst] = useState(false);
  const [inclusivePrice, setInclusivePrice] = useState(null);
  const watchedTaxPer = Form.useWatch("tax_per", form);
  const taxLabel = isIndia ? "GST" : "VAT";
  const taxMissing =
    watchedTaxPer === undefined || watchedTaxPer === null || watchedTaxPer === "";

  const gstEnabled = authInfo?.gst_enabled === "yes";
  const showTax = !isIndia || gstEnabled;
  const isGSTDisabled =
    isIndia && authInfo?.gst_enabled === "no";
  const isGSTEnabled =
    isIndia && authInfo?.gst_enabled === "yes";

  useEffect(() => {
    dispatch(fetchItemCode());
    dispatch(fetchGenericNames());
    dispatch(fetchItemGroups());
    dispatch(fetchItemCategories());
    dispatch(fetchItemModels());
    dispatch(fetchItemOrigins());
    if (!units.length) dispatch(fetchItemUnits());
    if (!racks.length) dispatch(fetchItemRacks());
    if (!branches.length) dispatch(fetchBranchList());
    if (!manufactures.length) dispatch(fetchManufactures());
    if (!distributors.length) dispatch(fetchDistributors());
  }, [dispatch,
    units.length,
    racks.length,
    branches.length,
    manufactures.length,
    distributors.length,
  ]);
  useEffect(() => {
    if (itemCode) {
      form.setFieldsValue({ item_code: itemCode });
    }
  }, [itemCode, form]);
  useEffect(() => {
    if (fromServiceEntry) {
      form.setFieldsValue({ is_service: true });
    }

  }, [fromServiceEntry, form]);

  // Reactive (unlike form.getFieldValue in render): service/asset items carry no
  // stock, so the opening fields are disabled and reset to 0 when the box is ticked.
  const isServiceItem = Form.useWatch("is_service", form) === true;
  const isAssetItem = Form.useWatch("is_asset", form) === true;
  const isNonStockItem = isServiceItem || isAssetItem;
  useEffect(() => {
    if (isNonStockItem) {
      form.setFieldsValue({ opening_qty: 0, opening_rate: 0 });
    }
  }, [isNonStockItem, form]);

  useEffect(() => {
    if (

      authInfo?.userInfo.branch_id &&
      branches?.length
    ) {
      form.setFieldValue("branch_list", [authInfo.userInfo.branch_id]);
    }
  }, [authInfo, branches, form]);

  // Retail Rate = inclusive price / (1 + tax%), same as the full item form.
  useEffect(() => {
    if (!priceInclGst) return;
    const tax = Number(watchedTaxPer);
    if (
      inclusivePrice === null ||
      inclusivePrice === undefined ||
      taxMissing ||
      Number.isNaN(tax)
    ) {
      return;
    }
    const exclusive = Number(inclusivePrice) / (1 + tax / 100);
    form.setFieldValue("sale_rate", Math.round(exclusive * 10000) / 10000);
  }, [priceInclGst, inclusivePrice, watchedTaxPer, taxMissing, form]);

  useEffect(() => {
    if (!pendingUnitName) return;

    const found = units.find(
      (u) => u.unit_name?.toLowerCase() === pendingUnitName
    );

    if (found) {
      form.setFieldValue("unit_id", found.unit_id);
      setPendingUnitName(null);
    }
  }, [units, pendingUnitName, form]);

  // useEffect(() => {
  //   if (fromServiceEntry) {
  //     // Example: set a default customer group or any other field
  //     form.setFieldsValue({
  //       customer_group_id: "SERVICE_PRODUCT_ID", // replace with actual value or logic
  //       // ...set other fields as needed
  //     });
  //     // You can also trigger any other logic here
  //   }
  // }, [fromServiceEntry, form]);
  const handleGenerateBarcode = () => {
    const code = String(Date.now()).slice(-12).padStart(12, "0");
    setGeneratedBarcode(code);
    setShowBarcodePreview(true);
    form.setFieldValue("item_barcode", code);
    setTimeout(() => {
      if (barcodeCanvasRef.current) {
        JsBarcode(barcodeCanvasRef.current, code, {
          format: "CODE128",
          lineColor: "#000",
          width: 2,
          height: 60,
          displayValue: true,
        });
      }
    }, 50);
  };

  const handleDownloadBarcode = () => {
    const canvas = barcodeCanvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `barcode-${generatedBarcode}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const onPhotoChange = (info) => {
    const fileObj = info.file?.originFileObj || info.file;
    if (!fileObj) return;

    setPhoto(fileObj);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(fileObj);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const isService = values.is_service === true;
    const isAsset = values.is_asset === true;
    const isNonStock = isService || isAsset;


    // Manual Validations (you can also move some to Form.Item rules)
    if (!values.item_name?.trim()) {
      return modal.warning({ title: "Product Name is Required" });
    }
    if (values.item_name.length > 100) {
      return modal.warning({ title: "Product Name max size exceeded" });
    }
    if (!values.unit_id) {
      return modal.warning({ title: "Select a Unit" });
    }
    if (values.narration?.length > 100) {
      return modal.warning({ title: "Description max size exceeded" });
    }
    // if (!isService && (!values.purchase_rate || values.purchase_rate == 0)) {
    //   return modal.warning({ title: "Purchase Rate is Required" });
    // }
    if (!isNonStock && (!values.sale_rate || values.sale_rate == 0)) {
      return modal.warning({
        title: (isDrugStore || isPharmacy)
          ? "Retail / MRP Rate is Required"
          : "Retail Rate is Required"
      });
    }
    if (!isNonStock && isDrugStore && (!values.gtn_num || values.gtn_num == 0)) {
      return modal.warning({ title: "GTN Number is Required" });
    }

    if (
      !isNonStock &&
      (values.opening_rate === undefined ||
        values.opening_rate === null ||
        values.opening_rate === "")
    ) {
      return modal.warning({ title: "Opening Rate is Required" });
    }

    if (
      !isNonStock &&
      (values.opening_qty === undefined ||
        values.opening_qty === null ||
        values.opening_qty === "")
    ) {
      return modal.warning({ title: "Opening Quantity is Required" });
    }

    if (
      !isNonStock &&
      showTax &&
      !isGSTDisabled &&
      (values.tax_per === undefined ||
        values.tax_per === null ||
        values.tax_per === "")
    ) {
      return modal.warning({ title: "Product VAT% is Required" });
    }

    if (values.branch_list?.length === 0) {
      return modal.warning({ title: "Please Provide Branch Details" });
    }

    const payload = {
      action: "create",
      item_code: values.item_code?.trim(),
      item_name: values.item_name.trim(),
      unit_id: values.unit_id,
      sale_rate: values.sale_rate,
      generic_name_id: values.generic_name_id || 0,
      purchase_rate: values.purchase_rate || 0,
      item_barcode: values.item_barcode?.trim() || "",
      tax_per: values.tax_per,
      hsn_sac_code: values.hsn_sac_code,
      is_service: values.is_service ? "yes" : "no",
      is_asset: values.is_asset ? "yes" : "no",
      rack_id: values.rack_id || 0,
      manufacture_id: values.manufacture_id || 0,
      distributer_id: values.distributer_id || 0,
      opening_rate: isNonStock ? 0 : values.opening_rate || 0,
      opening_qty: isNonStock ? 0 : values.opening_qty || 0,
      branch_list: values.branch_list || [],
      wholesaler_rate: values.wholesaler_rate || 0,
      corporate_rate: values.corporate_rate || 0,
      distributor_rate: values.distributor_rate || 0,

      discount_per: (isDrugStore || isPharmacy) ? (values.discount_per || 0) : 0, // ✅ was missing
      gtn_num: values.gtn_num || 0,
      group_id: values.group_id || 0,
      category_id: values.category_id || 0,
      model_id: values.model_id || 0,
      origin_id: values.origin_id || 0,
      mrp_rate: values.mrp_rate,
      moh_invoice_rate: isDrugStore ? (values.moh_invoice_rate || 0) : undefined,
      return_days: values.return_days || 0,
      narration: values.narration,
    };

    // A photo can only travel as multipart, so switch to FormData (same shape the
    // full item form sends) when one is attached; otherwise keep the JSON body.
    let body = payload;
    if (photo instanceof File) {
      body = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        // backend expects comma-separated string
        body.append(key, Array.isArray(value) ? value.join(",") : value);
      });
      body.append("photo", photo);
    }

    try {
      const res = await dispatch(createOrUpdateItem(body)).unwrap();
      modal.success({ title: "Product created" });
      // refresh products in lookup slice & itemManage slice
      await dispatch(fetchItems());

      const list = await dispatch(fetchItemsList()).unwrap();

      const updatedItems = store.getState().lookups.items;

      const created = updatedItems.find(
        (i) => i.item_name === payload.item_name
      );

      onSuccess?.(created);
    } catch (err) {
      modal.error({ title: err.message || "Failed to create product" });
    }
  };

  const handleSaveManufacture = async () => {
    if (!manufactureName?.trim()) return;

    try {
      const res = await dispatch(
        createManufactures({
          action: "create",
          name: manufactureName.trim(),
          company_prefix_length: manufactureGcpLength || null,
        })
      ).unwrap();

      // 🔄 Refresh manufacture list
      const list = await dispatch(fetchManufactures()).unwrap();

      // 🎯 Find newly created one
      const created = list.find(
        (m) => m.name.toLowerCase() === manufactureName.trim().toLowerCase()
      );

      if (created) {
        form.setFieldsValue({
          manufacture_name: created.name,
          manufacture_id: created.id,
        });
      }

      setManufactureName("");
      setManufactureGcpLength(null);
      setOpenManufactureModal(false);
    } catch (err) {
      modal.error({ title: err.message || "Failed to create manufacture" });
    }
  };

  const handleSaveDistributor = async () => {
    if (!distributorName?.trim()) return;

    try {
      const res = await dispatch(
        createDistributors({
          action: "create",
          name: distributorName.trim(),
        })
      ).unwrap();

      const list = await dispatch(fetchDistributors()).unwrap();

      const created = list.find(
        (d) => d.name.toLowerCase() === distributorName.trim().toLowerCase()
      );

      if (created) {
        form.setFieldsValue({
          distributer_name: created.name,
          distributer_id: created.id,
        });
      }

      setDistributorName("");
      setOpenDistributorModal(false);
    } catch (err) {
      modal.error({ title: err.message || "Failed to create distributor" });
    }
  };

  const handleSaveUnit = async (values) => {
    await dispatch(
      createOrUpdateUnit({
        action: "create",
        unit_id: 0,
        unit_name: values.unit_name,
        unit_symbol: values.unit_symbol,
      })
    ).unwrap();

    setPendingUnitName(values.unit_name.trim().toLowerCase());

    dispatch(fetchItemUnits());

    setOpenMasterModal(false);
  };



  return (
    <>
      <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off" className="drawer-form product-drawer-form" initialValues={{
        is_service: fromServiceEntry ? true : false,
      }}>

        <Form.Item name="is_service" valuePropName="checked">
          <Checkbox disabled={isAssetItem}>Service Item</Checkbox>
        </Form.Item>

        {/* Asset Item -- non-stock, purchasable via Purchase Bill only, posts to a
            user-chosen Fixed Asset account. Mutually exclusive with Service Item. */}
        <Form.Item name="is_asset" valuePropName="checked">
          <Checkbox disabled={isServiceItem}>Asset Item</Checkbox>
        </Form.Item>

        <Form.Item label="Item Code" name="item_code">
          <Input disabled />
        </Form.Item>

        <Form.Item
          label="Product Name"
          name="item_name"
          rules={[{ required: true, message: "Product Name is Required" }]}
        >
          <Input />
        </Form.Item>

        {/* Generic Name — Pharmacy (all countries) + Drug Store UAE only */}
        {(isDrugStore || isPharmacy) && (
          <Form.Item
            label="Product Generic Name"
            name="generic_name_id"
          >
            <Select
              placeholder="Select generic name"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? "")
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onSelect={(value) => {
                if (value === "add_new") {
                  form.setFieldValue("generic_name_id", undefined);
                  setOpenGenericModal(true);
                }
              }}
            >
              <Select.Option value="add_new">
                + Add New
              </Select.Option>

              {sortedGenericNames.map((g) => (
                <Select.Option
                  key={g.generic_name_id}
                  value={g.generic_name_id}
                >
                  {g.generic_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item label="Group" name="group_id">
          <Select
            placeholder="Select group"
            allowClear
            showSearch
            filterOption={filterByLabel}
            onChange={() =>
              form.setFieldsValue({ category_id: undefined, model_id: undefined })
            }
          >
            {(groups || []).map((g) => (
              <Select.Option key={g.group_id} value={g.group_id}>
                {g.group_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Category" name="category_id">
          <Select
            placeholder={selectedGroupId ? "Select category" : "Select a group first"}
            disabled={!selectedGroupId}
            allowClear
            showSearch
            filterOption={filterByLabel}
            onChange={() => form.setFieldsValue({ model_id: undefined })}
          >
            {filteredCategories.map((c) => (
              <Select.Option key={c.category_id} value={c.category_id}>
                {c.category_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label={(isDrugStore || isPharmacy) ? "Sub Category" : "Model"} name="model_id">
          <Select
            placeholder={selectedCategoryId ? "Select" : "Select a category first"}
            disabled={!selectedCategoryId}
            allowClear
            showSearch
            filterOption={filterByLabel}
          >
            {filteredModels.map((m) => (
              <Select.Option key={m.model_id} value={m.model_id}>
                {m.model_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Rack" name="rack_id">
          <Select
            options={racks.map((r) => ({
              label: r.name,
              value: r.id,
            }))}
          />
        </Form.Item>

        <Form.Item label="Origin" name="origin_id">
          <Select placeholder="Select origin" allowClear>
            {(origins || []).map((o) => (
              <Select.Option key={o.origin_id} value={o.origin_id}>
                {o.origin_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="Barcode" name="item_barcode">
          <Input placeholder="Enter barcode" />
        </Form.Item>

        <Form.Item>
          <Button
            type="dashed"
            onClick={handleGenerateBarcode}
            disabled={!!form.getFieldValue("item_barcode")}
          >
            Generate Barcode
          </Button>
        </Form.Item>

        {showBarcodePreview && (
          <Form.Item>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start" }}>
              <canvas ref={barcodeCanvasRef} />
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={handleDownloadBarcode}
              >
                Download Barcode
              </Button>
            </div>
          </Form.Item>
        )}
        <Form.Item
          label="Unit"
          name="unit_id"
          rules={[
            {
              required: true,
              message: "Select Unit",
            },
          ]}
        >
          <Select
            placeholder="Select Unit"
            onSelect={(value) => {
              if (value === "add_new") {
                form.setFieldValue("unit_id", undefined);
                setOpenMasterModal(true);
              }
            }}
          >
            <Select.Option value="add_new">
              + Add New
            </Select.Option>

            {units.map((u) => (
              <Select.Option
                key={u.unit_id}
                value={u.unit_id}
              >
                {u.unit_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* GTN No — Drug Store or Pharmacy, non-service/asset only */}
        {!isNonStockItem &&
          (authInfo?.company_type === "Drug Store" ||
            authInfo?.company_type === "Pharmacy") && (
            <Form.Item
              label="GTN No"
              name="gtn_num"
              rules={[{ required: true, message: "GTN Number is Required" }]}
            >
              <InputNumberField min={0} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          )}

        {/* Hidden Fields for manufacture and distributor */}
        <Form.Item name="manufacture_name" hidden><input /></Form.Item>
        <Form.Item name="manufacture_id" hidden><input /></Form.Item>
        <Form.Item name="distributer_name" hidden><input /></Form.Item>
        <Form.Item name="distributer_id" hidden><input /></Form.Item>

        <Form.Item label="Manufacture">
          <SearchListAutoComplete
            entityType="manufacture"
            placeholder="Manufacture"
            showAddNew={true}
            optionsList={manufactures.map((m) => ({
              sale_id: m.id,
              display_text: m.name,
              raw: m,
            }))}
            value={
              form.getFieldValue("manufacture_name")
                ? { display_text: form.getFieldValue("manufacture_name") }
                : null
            }
           onSelect={(val) => {
              if (!val) {
                form.setFieldsValue({
                  manufacture_name: "",
                  manufacture_id: 0,
                });
                return;
              }
              if (val === "add_new") {
                setOpenManufactureModal(true);
                return;
              }
              form.setFieldsValue({
                manufacture_name: val.display_text,
                manufacture_id: val.raw.id,
              });
            }}
          />
        </Form.Item>

        <Form.Item label="Distributor">
          <SearchListAutoComplete
            entityType="distributor"
            placeholder="Distributor"
            showAddNew={true}
            optionsList={distributors.map((d) => ({
              sale_id: d.id,
              display_text: d.name,
              raw: d,
            }))}
            value={
              form.getFieldValue("distributer_name")
                ? { display_text: form.getFieldValue("distributer_name") }
                : null
            }
            onSelect={(val) => {
              if (!val) {
                form.setFieldsValue({
                  distributer_name: "",
                  distributer_id: 0,
                });
                return; 
              }
              if (val === "add_new") {
                setOpenDistributorModal(true);
                return;
              }
              form.setFieldsValue({
                distributer_name: val.display_text,
                distributer_id: val.raw.id,
              });
            }}
          />
        </Form.Item>

        {showTax && (
          <Form.Item
            label={isIndia ? "Product GST %" : "Product VAT %"}
            name="tax_per"
            help={isGSTDisabled ? "Product GST is disabled for your account" : ""}
            rules={
              isGSTDisabled
                ? []
                : [
                  {
                    required: true,
                    message: isIndia
                      ? "Product GST % is Required"
                      : "Product VAT % is Required",
                  },
                ]
            }
          >
            {isIndia ? (
              <SelectField
                placeholder="Select GST %"
                disabled={isGSTDisabled}
              >
                <Option  value={0}>GST 0%</Option>
                <Option value={5}>GST 5%</Option>
                <Option value={18}>GST 18%</Option>
                <Option value={40}>GST 40%</Option>
              </SelectField>
            ) : (
              <InputNumberField
                min={0}
                max={100}
                style={{ width: "100%" }}
                disabled={isGSTDisabled}
              />
            )}
          </Form.Item>
        )}

        {showTax && !isGSTDisabled && (
          <div style={{ marginBottom: priceInclGst ? 8 : 16 }}>
            <Checkbox
              checked={priceInclGst}
              onChange={(e) => {
                setPriceInclGst(e.target.checked);
                if (!e.target.checked) setInclusivePrice(null);
              }}
            >
              {`Enter price including ${taxLabel}`}
            </Checkbox>
          </div>
        )}
        {showTax && !isGSTDisabled && priceInclGst && (
          <Form.Item
            label={`Price incl. ${taxLabel}${taxMissing ? "" : ` (${watchedTaxPer}%)`}`}
            validateStatus={taxMissing ? "warning" : undefined}
            help={
              taxMissing
                ? `Select Product ${taxLabel} % above to calculate the Retail Rate`
                : undefined
            }
          >
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              value={inclusivePrice}
              onChange={(v) => setInclusivePrice(v)}
            />
          </Form.Item>
        )}

        {/* Retail Rate — default (not Drug Store UAE, not Pharmacy) */}
        {!isDrugStore && !isPharmacy && (
          <Form.Item
            label={`Retail Rate${priceInclGst ? ` (excl. ${taxLabel}, auto-calculated)` : ""}`}
            name="sale_rate"
            rules={[{ required: true, message: "Retail Rate is Required" }]}
          >
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              precision={priceInclGst ? 4 : undefined}
              disabled={priceInclGst}
            />
          </Form.Item>
        )}

        {/* Retail / MRP Rate — Pharmacy (all countries) + Drug Store UAE */}
        {(isDrugStore || isPharmacy) && (
          <Form.Item
            label={`Retail / MRP Rate${priceInclGst ? ` (excl. ${taxLabel}, auto-calculated)` : ""}`}
            name="sale_rate"
            rules={[{ required: true, message: "Retail / MRP Rate is Required" }]}
          >
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              precision={priceInclGst ? 4 : undefined}
              disabled={priceInclGst}
            />
          </Form.Item>
        )}
        {/* Discount % — Pharmacy (all countries) + Drug Store UAE only */}
        {(isDrugStore || isPharmacy) && (
          <Form.Item
            label="Discount %"
            name="discount_per"
            initialValue={0}
          >
            <InputNumberField
              min={0}
              max={100}
              style={{ width: "100%" }}
              suffix="%"
            />
          </Form.Item>
        )}
        {/* MRP — hide for Pharmacy and Drug Store UAE */}
        {!isDrugStore && !isPharmacy && (
          <Form.Item label="MRP" name="mrp_rate">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        )}

        {/* Wholesaler → Class A / Net Selling Rate (Drug Store UAE only) */}
        {isDrugStore ? (
          <Form.Item label="Class A / Net Selling Rate" name="wholesaler_rate">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        ) : !isPharmacy ? (
          <Form.Item label="Wholesaler Rate" name="wholesaler_rate">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        ) : null}

        {/* Corporate → Class B Rate (Drug Store UAE only) */}
        {isDrugStore ? (
          <Form.Item label="Class B Rate" name="corporate_rate">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        ) : !isPharmacy ? (
          <Form.Item label="Corporate Rate" name="corporate_rate">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        ) : null}

        {/* Distributor → Class C Rate (Drug Store UAE only) */}
        {isDrugStore ? (
          <Form.Item label="Class C Rate" name="distributor_rate">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        ) : !isPharmacy ? (
          <Form.Item label="Distributor Rate" name="distributor_rate">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        ) : null}

        {/* Opening Stock MOH Rate / Rate — disabled for services */}
        <Form.Item
          label={(isDrugStore || isPharmacy)
            ? "Opening Stock Net Cost Per Quantity"
            : "Opening Stock Rate Per Quantity"}
          name="opening_rate"
          initialValue={0}
          rules={[{ required: !isNonStockItem, message: "Opening Stock Rate is Required" }]}
        >
          <InputNumber min={0} style={{ width: "100%" }} disabled={isNonStockItem} />
        </Form.Item>

        {isDrugStore && (
          <Form.Item label="MOH Invoice Rate" name="moh_invoice_rate">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        )}

        <Form.Item
          label="Opening Stock Qty"
          name="opening_qty"
          initialValue={0}
          rules={[{ required: !isNonStockItem, message: "Opening Stock Quantity is Required" }]}
        >
          <InputNumber min={0} precision={0} style={{ width: "100%" }} disabled={isNonStockItem} />
        </Form.Item>

        <Form.Item label="Return Policy" name="return_days">
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>

        {/* <Form.Item
        label={isIndia ? "GST1%" : "VAT %"}
        name="tax_per"
        rules={[{ required: true, message: isIndia ? "Product GST is Required" : "Product VAT % is Required" }]}
      >
        <InputNumber min={0} max={100} style={{ width: "100%" }} />
      </Form.Item> */}

        {isGSTEnabled && (
          <Form.Item
            label="HSN/SAC Code"
            name="hsn_sac_code"
            rules={[
              {
                required: true,
                message: "HSN/SAC Code is Required"
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();

                  const str = String(value).trim();

                  if (!/^[0-9]{4,8}$/.test(str)) {
                    return Promise.reject(
                      "Enter valid HSN/SAC Code (4–8 digits)"
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputField placeholder="Enter HSN/SAC Code" />
          </Form.Item>)}

        <Form.Item label="Narration / Description" name="narration">
          <Input.TextArea rows={3} />
        </Form.Item>

        <Form.Item label="Product Photo">
          <Upload
            beforeUpload={(file) => {
              if (!file.type.startsWith("image/")) {
                message.error("Only image files are allowed!");
                return Upload.LIST_IGNORE;
              }
              if (file.size / 1024 / 1024 >= 2) {
                message.error("Image must be smaller than 2MB!");
                return Upload.LIST_IGNORE;
              }
              if (file.name.length > 35) {
                modal.warning({
                  title: "Image file name is too long",
                  content: "Please rename the file to a shorter name and try again.",
                });
                return Upload.LIST_IGNORE;
              }
              return false; // prevent auto upload
            }}
            showUploadList={false}
            onChange={onPhotoChange}
            accept="image/*"
          >
            <Button icon={<UploadOutlined />}>Upload Photo</Button>
          </Upload>

          {preview && (
            <img
              src={preview}
              alt="Preview"
              style={{ width: 100, marginTop: 8, marginLeft: 8, borderRadius: 4 }}
            />
          )}
        </Form.Item>

        <Form.Item
          label="Choose Branch"
          name="branch_list"
          rules={[{ required: true, message: "Choose a Branch" }]}
        >
          <Select
            mode="multiple"
            placeholder="Select branches"
            options={branches.map((b) => ({
              label: b.name,
              value: b.id,
            }))}
          />
        </Form.Item>

      </Form>

      <Modal
        open={openManufactureModal}
        title="Add Manufacture"
        onCancel={() => setOpenManufactureModal(false)}
        footer={null}
        bodyStyle={{ padding: 20 }}
      >
        <Input
          value={manufactureName}
          onChange={(e) => setManufactureName(e.target.value)}
          placeholder="Enter Manufacture Name"
        />
        <InputNumber
          min={6}
          max={12}
          precision={0}
          value={manufactureGcpLength}
          onChange={(value) => setManufactureGcpLength(value)}
          placeholder="Company Prefix Length (6-12, optional)"
          style={{ width: "100%", marginTop: 12 }}
        />
        <Button
          type="primary"
          block
          style={{ marginTop: 12 }}
          onClick={handleSaveManufacture}
        >
          Save
        </Button>
      </Modal>

      <Modal
        open={openDistributorModal}
        title="Add Distributor"
        onCancel={() => setOpenDistributorModal(false)}
        footer={null}
        bodyStyle={{ padding: 20 }}
      >
        <Input
          value={distributorName}
          onChange={(e) => setDistributorName(e.target.value)}
          placeholder="Enter Distributor Name"
        />
        <Button
          type="primary"
          block
          style={{ marginTop: 12 }}
          onClick={handleSaveDistributor}
        >
          Save
        </Button>
      </Modal>
      <Modal
        open={openGenericModal}
        title="Add Generic Name"
        onCancel={() => setOpenGenericModal(false)}
        footer={null}
      >
        <Input
          value={genericName}
          onChange={(e) => setGenericName(e.target.value)}
          placeholder="Enter Generic Name"
        />

        <Button
          type="primary"
          block
          style={{ marginTop: 12 }}
          onClick={async () => {
            if (!genericName?.trim()) return;

            try {
              await dispatch(
                saveGenericName({
                  action: "create",
                  generic_name_id: 0,
                  generic_name: genericName.trim(),
                })
              ).unwrap();

              const list = await dispatch(fetchGenericNames()).unwrap();

              const created = list.find(
                (g) =>
                  g.generic_name.toLowerCase() ===
                  genericName.trim().toLowerCase()
              );

              if (created) {
                form.setFieldsValue({
                  generic_name_id: created.generic_name_id,
                });
              }

              setGenericName("");
              setOpenGenericModal(false);
            } catch (err) {
              modal.error({
                title: err.message || "Failed to create generic name",
              });
            }
          }}
        >
          Save
        </Button>
      </Modal>

      <AddMasterModal
        open={openMasterModal}
        onClose={() => setOpenMasterModal(false)}
        onSave={handleSaveUnit}
        title="Add New UNIT"
        masterType="unit"
      />
    </>
  );
};


export default ProductQuickForm;